import 'intl-pluralrules';
import * as i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { z } from 'zod';
import { makeZodI18nMap } from 'zod-i18n-map';
import ChainedBackend from 'i18next-chained-backend';
import HttpBackend from 'i18next-http-backend';
import AsyncStorageBackend from 'i18next-async-storage-backend2';

import { LanguageDetector } from '../languageDetector';
import { DEFAULT_OPTIONS, getResourcesWrappedWithNamespace } from '../lib/createInstance';
import { FORM_NS, getResourcesWithZod } from '../../zod';
import { LanguageKey, LanguagesResources } from '../../consts';
import { reloadInterpolations } from '../events/reloadInterpolations';

const DEFAULT_TRANSLATIONS_PATH = '/locales/translations/{{lng}}/{{ns}}.json';

export function i18nInit({ resources, ...options }: Options, otherInstances?: Instance[]) {
	void i18n
		.use(ChainedBackend)
		.use(LanguageDetector)
		.use(initReactI18next)
		// for all options read: https://www.i18next.com/overview/configuration-options
		.init({
			...DEFAULT_OPTIONS,
			resources: getResourcesWithZod(
				getResourcesWrappedWithNamespace({ resources, defaultNS: options.defaultNS }),
			),
			backend: {
				backends: [AsyncStorageBackend, HttpBackend],
				backendOptions: [
					{
						expirationTime: 7 * 24 * 60 * 60 * 1000, // 7 days
					},
					{
						loadPath: DEFAULT_TRANSLATIONS_PATH,
					},
				],
			},
			...options,
		});

	otherInstances &&
		i18n.default.on('languageChanged', (newLanguage: LanguageKey) =>
			Promise.all(otherInstances.map(({ changeLanguage }) => changeLanguage(newLanguage))),
		);

	i18n.default.on(
		'reloadInterpolations',
		reloadInterpolations([
			i18n.default,
			...((otherInstances?.filter(isI18nInstance) || []) as i18n.i18n[]),
		]),
	);
	z.setErrorMap(
		makeZodI18nMap({
			ns: ['zod', FORM_NS],
			handlePath: {
				keyPrefix: 'paths',
			},
		}),
	);

	return i18n;
}

type i18nInstance = i18n.i18n;
function isI18nInstance(instance: Instance): instance is i18nInstance {
	return (<i18nInstance>instance).init !== undefined;
}
type Instance = { changeLanguage: (newLanguage: LanguageKey) => void } | i18n.i18n;

type Options = Omit<NonNullable<Parameters<typeof i18n.init>['0']>, 'resources'> & {
	resources: LanguagesResources;
};
